import React, { useState } from 'react'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'

// Components
import Form from 'components/Form'
import Button from 'components/Button'
import Input from 'components/Input'
import { useToast } from 'containers/ToastManager'
import LoadingState from 'components/LoadingState'
import { SsoSignInSource } from 'modules/sso/SsoSignInSource'

import { useGetSsoStartUrl } from '../../operations'

// Styles
import { StyledFormField } from './SsoSignInForm.styles'

// Validation
const signInSchema = yup.object().shape({
  teamName: yup.string().trim().required('Workspace name can’t be blank'),
})

const INITIAL_VALUES = {
  teamName: '',
}

// Stypes
type Values = typeof INITIAL_VALUES

const TeamNameField = ({
  touched,
  errors,
  values,
  handleChange,
  handleBlur,
}: FormikProps<Values>) => (
  <StyledFormField
    name="teamName"
    errorText={touched.teamName ? errors.teamName : undefined}
    help="Up to 16 letters and numbers, including hyphens but no spaces">
    <Input
      name="teamName"
      type="text"
      placeholder="SSO-Shortname"
      value={values.teamName}
      onChange={handleChange}
      onBlur={handleBlur}
      autoFocus
    />
  </StyledFormField>
)

export const Errors = ({ errors }: { errors: React.ReactNode[] }) =>
  errors.length > 0 ? <Form.ErrorField>{errors[0]}</Form.ErrorField> : null

const Submit = ({ isLoading }: { isLoading: boolean }) => (
  <Form.Footer>
    <Button
      variant="primary-untinted"
      type="submit"
      disabled={isLoading}
      loading={isLoading}
      smaller
      fill>
      Next
    </Button>
  </Form.Footer>
)

const SsoSignInForm = ({
  source,
  from,
  teamName,
}: {
  source: SsoSignInSource
  from?: string
  teamName?: string
}) => {
  const [showServerErrors, setShowServerErrors] = useState(false)
  const { showToast } = useToast()
  const { getUrl, response, isLoading } = useGetSsoStartUrl({
    source,
    from,
    onNetworkError: error => showToast(error, 'negative'),
    onCompleted: () => {
      setShowServerErrors(true)
    },
  })

  if (response.url) {
    window.location.href = response.url
  }

  if (teamName && (response.url || isLoading)) {
    return <LoadingState />
  }

  return (
    <Formik
      initialValues={{ ...INITIAL_VALUES, teamName: teamName || '' }}
      onSubmit={getUrl}
      validationSchema={signInSchema}
      validateOnBlur={false}>
      {formikProps => (
        <Form>
          <TeamNameField
            {...formikProps}
            handleChange={(e: React.ChangeEvent<any>) => {
              if (showServerErrors) {
                setShowServerErrors(false)
              }

              return formikProps.handleChange(e)
            }}
          />
          <Errors errors={showServerErrors ? response.errors : []} />
          <Submit isLoading={isLoading} />
        </Form>
      )}
    </Formik>
  )
}

export default SsoSignInForm
